import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getInrichtingDetail, getPutten, getGebieden, verwerkWijzigingsbesluit,
} from '../api/api';
import { fmtId, fmtDatumNL } from '../util/format';
import { categorieenVanDoelen } from '../util/doelen';
import DoelenKiezer from '../components/DoelenKiezer';
import PuttenKaart from '../components/PuttenKaart';
import Kruimels from '../components/Kruimels';
import { nieuweSleutel } from '../components/PuttenPaneel';

/* Wijzigingsbesluit verwerken — een pagina, geen venster.

   Dit zat in een modal met een handgemaakte puttenlijst: per put een
   schakelaar blijft/verwijderen, en toevoegen via een putnummer of een paar
   coördinaatvelden. Geen kaart, geen perceel, geen gebiedscheck — terwijl een
   besluit juist over plekken gaat.

   Nu dezelfde opbouw als een vergunning aanmaken, met hetzelfde puttenpaneel.
   Wat er anders is dan daar: de putten van de inrichting staan er al in, en
   die kun je niet "uit de selectie" halen maar uit de ínrichting. Dat is een
   besluit met een datum, geen correctie van een vergissing in dit scherm, en
   daarom blijven ze zichtbaar — grijs, doorgestreept, met de datum erbij.

   Deze pagina is alleen om te bewerken. Wat er klaarstaat bekijk je in het
   venster op de inrichtingpagina; daar zit ook de knop die je hierheen brengt.
   Eén stand dus, en niet een pagina die soms wel en soms niet reageert.

   ── De wachtrij ────────────────────────────────────────────────────────────
   Er kan meer dan één besluit klaarstaan. Zonder gepland_id in de route schuif
   je er eentje achteraan; met een gepland_id bewerk je dát besluit.

   Een besluit beschrijft de situatie zoals die op zíjn dag geldt. Staat er al
   een besluit vóór dit besluit klaar, dan is de uitgangssituatie hier dus niet
   de huidige registratie maar wat er ná dat eerdere besluit staat: zijn putten
   erbij, de zijne eruit, en zijn doelen en debieten als vertrekpunt. */

const getal = (v) => (String(v ?? '').trim() === '' ? null : Number(v));

/* Een put uit het register of uit de puttenlijst van de inrichting, in het
   model van het paneel. */
const uitRegisterPut = (p, extra = {}) => ({
  sleutel: nieuweSleutel(),
  soort: 'gekoppeld',
  put_id: p.put_id,
  uitRegister: true,
  x_rd: +p.x_rd,
  y_rd: +p.y_rd,
  perceel: p.perceel || null,
  max_diepte_mv: p.max_diepte_mv != null ? String(p.max_diepte_mv) : '',
  opgegeven_diepte_m: p.opgegeven_diepte_m != null ? String(p.opgegeven_diepte_m) : '',
  opgegevenDiepteLeeg: p.opgegeven_diepte_m == null,
  besluitnummer: p.besluitnummer_wsw || '',
  kandidaten: [],
  herkomst: 'overgenomen',
  ...extra,
});

export default function WijzigingsbesluitPagina() {
  const { id, geplandId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [laadFout, setLaadFout] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);

  const [wbNummer, setWbNummer] = useState('');
  const [ingangsdatum, setIngangsdatum] = useState('');
  const [doelen, setDoelen] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [debiet, setDebiet] = useState({ uur: '', maand: '', kwartaal: '', jaar: '' });
  const [bedrijfsomvang, setBedrijfsomvang] = useState('');
  const [opmerkingen, setOpmerkingen] = useState('');
  const [putten, setPutten] = useState([]);
  const [registerPutten, setRegisterPutten] = useState([]);
  const [gebieden, setGebieden] = useState([]);
  // Het besluit waar dit besluit op voortbouwt, als dat er is.
  const [voorganger, setVoorganger] = useState(null);

  useEffect(() => {
    /* Detail en registerputten samen: een geplande wijziging kan een
       registerput toevoegen, en die staat in de wachtkamer alleen als nummer.
       Zonder de puttenlijst weten we niet waar hij ligt en kunnen we hem niet
       op de kaart zetten. */
    Promise.all([getInrichtingDetail(id), getPutten()])
      .then(([r, rp]) => {
        const alle = (rp.data || []).filter((q) => !q.buiten_gebruik_datum);
        setRegisterPutten(alle);
        const d = r.data;
        setDetail(d);
        const i = d.inrichting || {};

        const wachtrij = d.geplande_wijzigingen || [];
        const positie = geplandId
          ? wachtrij.findIndex((x) => x.gepland_id === geplandId)
          : wachtrij.length;
        if (geplandId && positie < 0) {
          setLaadFout('Dit geplande wijzigingsbesluit bestaat niet meer. '
            + 'Mogelijk is het inmiddels ingegaan of geannuleerd.');
          return;
        }
        const g = geplandId ? wachtrij[positie] : null;
        // Alles wat vóór dit besluit ingaat. Daar bouwt dit besluit op voort.
        const eerder = wachtrij.slice(0, positie);
        const vorige = eerder.length ? eerder[eerder.length - 1] : null;
        setVoorganger(vorige);

        // Doelen en debieten: van dit besluit als het er al is, anders van de
        // voorganger, anders van de lopende vergunning.
        const bron = g || vorige || i;
        setWbNummer(g?.wijzigingsbesluitnummer || '');
        setIngangsdatum(g?.ingangsdatum || '');
        setDoelen(bron.doelen || []);
        setCategorieen(categorieenVanDoelen(bron.doelen || []));
        setDebiet({
          uur: bron.vergund_debiet_uur ?? '',
          maand: bron.vergund_debiet_maand ?? '',
          kwartaal: bron.vergund_debiet_kwartaal ?? '',
          jaar: bron.vergund_debiet_jaar ?? '',
        });
        setBedrijfsomvang(bron.bedrijfsomvang_ha ?? i.bedrijfsomvang_ha ?? '');
        setOpmerkingen(bron.opmerkingen || '');

        /* Waar we de gegevens van een put kunnen opzoeken: eerst de putten van
           deze inrichting — daar zitten de aankomende ook bij — dan het
           register. */
        const info = new Map();
        (d.putten || []).forEach((p) => info.set(p.put_id, p));
        alle.forEach((q) => { if (!info.has(q.put_id)) info.set(q.put_id, q); });

        /* De uitgangssituatie: de putten die nu aan de inrichting hangen, met
           de eerdere besluiten er overheen. */
        let basis = (d.putten || [])
          .filter((p) => p.actief !== false && !p.aankomend_vanaf)
          .map((p) => p.put_id);
        eerder.forEach((e) => {
          const eruitBij = new Set(e.put_ids_verwijderen || []);
          basis = basis.filter((pid) => !eruitBij.has(pid));
          (e.putten_toevoegen || []).forEach((t) => {
            if (t.put_id && !basis.includes(t.put_id)) basis.push(t.put_id);
          });
        });

        /* `bestaandeKoppeling` onderscheidt de uitgangssituatie van wat je hier
           toevoegt: alleen deze kunnen uit de inrichting worden gehaald. */
        const eruit = new Set(g?.put_ids_verwijderen || []);
        const bestaand = basis
          .map((pid) => info.get(pid))
          .filter(Boolean)
          .map((p) => uitRegisterPut(p, {
            bestaandeKoppeling: true,
            verwijderd: eruit.has(p.put_id),
          }));

        /* En wat er in dit besluit al aan toevoegingen stond. Zonder dit zou
           bewerken de eerder opgegeven putten stilzwijgend laten vallen —
           precies het soort verlies dat je pas na opslaan ziet.

           Een put die dit besluit zélf heeft aangemaakt komt terug als "te
           boren", niet als registerput. Hij heeft wel een nummer — dat krijgt
           hij bij het klaarzetten, zodat hij een pagina heeft en zichtbaar is —
           maar hij ligt er nog niet, en zolang het besluit niet geldt mag je
           zijn plek en diepte nog bijstellen. */
        const erbij = (g?.putten_toevoegen || []).map((t) => {
          const p = t.put_id ? info.get(t.put_id) : null;
          if (!p) {
            // Een wachtkamerrij van vóór de putnummers: alleen coördinaten.
            return t.x_rd != null && t.y_rd != null ? {
              sleutel: nieuweSleutel(),
              soort: 'nieuw',
              put_id: null,
              uitRegister: false,
              bestaandeKoppeling: false,
              x_rd: +t.x_rd,
              y_rd: +t.y_rd,
              perceel: t.perceel || null,
              max_diepte_mv: t.max_diepte_mv != null ? String(t.max_diepte_mv) : '',
              opgegeven_diepte_m: '',
              besluitnummer: '',
              kandidaten: [],
              herkomst: 'overgenomen',
            } : null;
          }
          return uitRegisterPut(p, {
            bestaandeKoppeling: false,
            ...(t.aangemaakt_voor_besluit ? {
              soort: 'nieuw',
              uitRegister: false,
              eigenVanBesluit: true,
              opgegeven_diepte_m: '',
            } : {}),
          });
        }).filter(Boolean);

        setPutten([...bestaand, ...erbij]);
      })
      .catch((e) => setLaadFout(e.response?.data?.detail || e.message));
    getGebieden(undefined, true).then((r) => setGebieden(r.data || [])).catch(() => {});
  }, [id, geplandId]);

  const datumTekst = useMemo(
    () => (ingangsdatum ? fmtDatumNL(ingangsdatum) : null), [ingangsdatum]);

  /* De vroegst mogelijke dag: morgen, en na een voorganger de dag ná die
     voorganger. De backend bewaakt dit ook — dit voorkomt alleen dat je het
     pas na opslaan te horen krijgt. */
  const vroegsteDatum = useMemo(() => {
    const eenDagLater = (d) => {
      const t = new Date(`${d}T00:00:00`);
      t.setDate(t.getDate() + 1);
      return t.toISOString().slice(0, 10);
    };
    const morgen = eenDagLater(new Date().toISOString().slice(0, 10));
    if (!voorganger) return morgen;
    const na = eenDagLater(voorganger.ingangsdatum);
    return na > morgen ? na : morgen;
  }, [voorganger]);

  const terug = () => navigate(`/beregening/inrichtingen/${id}`);

  const opslaan = async () => {
    setFout(null);
    if (!wbNummer.trim()) return setFout('Wijzigingsbesluitnummer is verplicht.');
    if (!ingangsdatum) return setFout('Ingangsdatum is verplicht.');

    setBezig(true);
    try {
      const body = {
        ...(geplandId ? { gepland_id: geplandId } : {}),
        wijzigingsbesluitnummer: wbNummer.trim(),
        ingangsdatum,
        doelen,
        vergund_debiet_uur: getal(debiet.uur),
        vergund_debiet_maand: getal(debiet.maand),
        vergund_debiet_kwartaal: getal(debiet.kwartaal),
        vergund_debiet_jaar: getal(debiet.jaar),
        bedrijfsomvang_ha: getal(bedrijfsomvang),
        opmerkingen: opmerkingen.trim() || null,
        put_ids_verwijderen: putten
          .filter((p) => p.bestaandeKoppeling && p.verwijderd)
          .map((p) => p.put_id),
        /* Alles wat hier is bijgekomen. Een registerput gaat op nummer mee — die
           registratie is niet aan dit scherm. Een put die dit besluit zelf heeft
           aangemaakt gaat mét zijn velden mee, want die mag je nog bijstellen
           zolang het besluit niet geldt. */
        putten_toevoegen: putten
          .filter((p) => !p.bestaandeKoppeling)
          .map((p) => (p.put_id && !p.eigenVanBesluit
            ? { put_id: p.put_id }
            : {
              ...(p.put_id ? { put_id: p.put_id } : {}),
              x_rd: Number(p.x_rd),
              y_rd: Number(p.y_rd),
              perceel: (p.perceel || '').trim() || null,
              max_diepte_mv: getal(p.max_diepte_mv),
              besluitnummer_wsw: (p.besluitnummer || '').trim() || null,
              /* Een put die nog geboord moet worden heeft geen werkelijke
                 diepte; die zou hier alleen maar een leeg veld overschrijven. */
              ...(p.soort === 'nieuw' ? {} : {
                opgegeven_diepte_m: getal(p.opgegeven_diepte_m),
              }),
              ...(p.put_id ? {} : {
                diepte_bv_filter_m: getal(p.diepte_bv_filter_m),
              }),
            })),
      };
      await verwerkWijzigingsbesluit(id, body);
      terug();
    } catch (e) {
      setFout(e.response?.data?.detail || e.message);
    } finally {
      setBezig(false);
    }
  };

  if (laadFout) {
    return (
      <div className="main-inner">
        <Kruimels stappen={[
          { label: fmtId(id), naar: `/beregening/inrichtingen/${id}` },
          { label: 'Wijzigingsbesluit' },
        ]} />
        <div className="alert alert-error">{laadFout}</div>
        <p style={{ marginTop: 12 }}>
          <button className="btn btn-ghost" onClick={terug}>Terug naar de inrichting</button>
        </p>
      </div>
    );
  }
  if (!detail) return <div className="empty-state"><span className="spinner" /> Laden…</div>;

  const inrichting = detail.inrichting;
  const houder = detail.houder;
  const houderNaam = houder
    ? (houder.naam || [houder.voornaam, houder.tussenvoegsel, houder.achternaam].filter(Boolean).join(' '))
    : null;
  const eruit = putten.filter((p) => p.verwijderd).length;
  const erbij = putten.filter((p) => !p.bestaandeKoppeling).length;

  return (
    <div className="main-inner main-inner-wide">
      <div className="page-header">
        <div>
          <Kruimels stappen={[
            { label: fmtId(id), naar: `/beregening/inrichtingen/${id}` },
            { label: wbNummer ? `Wijzigingsbesluit ${wbNummer}` : 'Nieuw wijzigingsbesluit' },
          ]} />
          <h1 className="page-title">
            {geplandId ? 'Klaarstaand wijzigingsbesluit bewerken' : 'Wijzigingsbesluit verwerken'}
          </h1>
          <p className="page-subtitle">
            {houderNaam} <span className="mono">{fmtId(inrichting.houder_id)}</span>
          </p>
        </div>
      </div>

      {/* Ligt er een besluit vóór dit besluit, dan hoort dat hier te staan en
          niet pas te blijken uit de puttenlijst. */}
      {voorganger && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          Er loopt al een wijzigingsbesluit met nummer{' '}
          <span className="mono">{voorganger.wijzigingsbesluitnummer}</span>. Dat treedt
          op {fmtDatumNL(voorganger.ingangsdatum)} in werking.
        </div>
      )}

      <div className="card">
        <div className="card-header"><h3 className="card-title">Besluit</h3></div>
        <div className="field-grid">
          <div className="field">
            <label className="field-label">Wijzigingsbesluitnummer</label>
            <input value={wbNummer} onChange={(e) => setWbNummer(e.target.value)}
              placeholder="bv. WB-V-2026-0081" />
          </div>
          <div className="field">
            <label className="field-label">Ingangsdatum</label>
            <input type="date" value={ingangsdatum} min={vroegsteDatum}
              onChange={(e) => setIngangsdatum(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Bedrijfsomvang (ha)</label>
            <input type="number" step="0.01" value={bedrijfsomvang}
              onChange={(e) => setBedrijfsomvang(e.target.value)} />
          </div>
        </div>
        <div className="field" style={{ marginTop: 20 }}>
          <label className="field-label">Opmerkingen</label>
          <textarea rows={3} value={opmerkingen}
            onChange={(e) => setOpmerkingen(e.target.value)} />
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Doelen</h3></div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Wat het besluit vaststelt. Overgenomen uit
          {voorganger ? ' het vorige geplande besluit' : ' de huidige vergunning'}; pas
          aan wat verandert.
        </div>
        <DoelenKiezer
          categorieen={categorieen}
          doelen={doelen}
          onChange={({ categorieen: c, doelen: d }) => { setCategorieen(c); setDoelen(d); }}
        />
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Vergund maximum debiet</h3></div>
        <div className="field-grid">
          {[['uur', 'Vergund uurdebiet (m³)'], ['maand', 'Vergund maanddebiet (m³)'],
            ['kwartaal', 'Vergund kwartaaldebiet (m³)'], ['jaar', 'Vergund jaardebiet (m³)']]
            .map(([sleutel, label]) => (
              <div className="field" key={sleutel}>
                <label className="field-label">{label}</label>
                <input type="number" step="0.01" value={debiet[sleutel]}
                  onChange={(e) => setDebiet((d) => ({ ...d, [sleutel]: e.target.value }))} />
              </div>
            ))}
        </div>
      </div>

      <div className="card mf-kaart">
        <PuttenKaart
          putten={putten}
          onChange={setPutten}
          registerPutten={registerPutten}
          gebieden={gebieden}
          hoogte={520}
          kwetsbaarBlokkeert={false}
          dieptesUitDoel={false}
          verwijderbaar
          verwijderDatum={datumTekst}
        />
        {(eruit > 0 || erbij > 0) && (
          <p style={{ margin: '12px 2px 0', fontSize: 13.5, color: 'var(--text-soft)' }}>
            Dit besluit haalt <strong>{eruit}</strong> {eruit === 1 ? 'put' : 'putten'} uit de
            inrichting en voegt er <strong>{erbij}</strong> toe
            {datumTekst ? `, met ingang van ${datumTekst}` : ''}.
          </p>
        )}
      </div>

      {fout && <div className="alert alert-error" style={{ marginTop: 4 }}>{fout}</div>}

      <footer className="mf-voet">
        <button className="btn btn-ghost" onClick={terug} disabled={bezig}>Annuleren</button>
        <button className="btn btn-primary" onClick={opslaan} disabled={bezig}>
          {bezig ? <><span className="spinner" /> Verwerken…</>
            : (geplandId ? 'Wijziging bijwerken' : 'Wijzigingsbesluit verwerken')}
        </button>
      </footer>
    </div>
  );
}
